import React, { useEffect, useLayoutEffect, useSyncExternalStore } from 'react';
import type { SSRAuthClient } from '@react-keycloak/ssr';
import { useKeycloak } from '@react-keycloak/ssr';
import { useTranslation } from 'react-i18next';
import { FullPageError } from '@/componentsv2/misc/FullPageSessionState';
import { invalidateValidatedOrganizations } from './accessValidation';
import { clearSessionEtags } from './axiosInterceptors';
import { clearUserScopedCaches, purgeLegacyAppDataTenantCaches, purgeLegacyCaches, sweepExpiredCache } from './cacheKeys';
import { migrateLegacyLastVisitedOrganization } from './lastVisitedOrganization';
import { isSessionLogoutLatched, subscribeSessionLogout } from './logout';
import { getOrganizationList, invalidateOrganizationList } from './organizationList';
import {
  getAuthGeneration,
  getSessionBoundaryGeneration,
  getSessionKeycloak,
  getSessionRealmRoles,
  getSessionSubject,
  hasCommittedSessionIdentity,
  setSessionKeycloak,
  startProactiveTokenRefresh,
  stopProactiveTokenRefresh,
  subscribeSessionIdentity,
} from './token';

export const KeycloakSessionBridge = ({ children }: { children?: React.ReactNode }) => {
  const { t } = useTranslation();
  const { keycloak, initialized } = useKeycloak();
  useSyncExternalStore(subscribeSessionIdentity, getAuthGeneration, getAuthGeneration);
  const logoutLatched = useSyncExternalStore(subscribeSessionLogout, isSessionLogoutLatched, isSessionLogoutLatched);
  const authenticatedClient = keycloak?.authenticated && keycloak.token ? keycloak : null;
  const requestedSubject = (authenticatedClient as (SSRAuthClient & { tokenParsed?: { sub?: string } }) | null)?.tokenParsed?.sub ?? null;
  const requestedRealmRoles = [
    ...((authenticatedClient as (SSRAuthClient & { tokenParsed?: { realm_access?: { roles?: string[] } } }) | null)?.tokenParsed?.realm_access
      ?.roles ?? []),
  ].sort();
  const identityCommitted = hasCommittedSessionIdentity();
  const identityDiffers =
    initialized &&
    (!identityCommitted ||
      getSessionKeycloak() !== authenticatedClient ||
      getSessionSubject() !== requestedSubject ||
      JSON.stringify(getSessionRealmRoles()) !== JSON.stringify(requestedRealmRoles));
  const identityBoundaryPending = identityCommitted && identityDiffers;

  useLayoutEffect(() => {
    if (!initialized || !identityDiffers || logoutLatched) return;

    const previousClient = getSessionKeycloak();
    const previousSubject = getSessionSubject();
    setSessionKeycloak(authenticatedClient);

    // A client replacement is an identity boundary even when the replacement
    // represents the same subject. Nothing validated or cached by the old
    // authenticated client may survive into the remounted provider tree.
    if (identityCommitted && (previousClient !== authenticatedClient || previousSubject !== requestedSubject)) {
      invalidateValidatedOrganizations();
      invalidateOrganizationList();
      if (previousSubject) clearUserScopedCaches(previousSubject);
      purgeLegacyAppDataTenantCaches();
      clearSessionEtags();
    }
  }, [authenticatedClient, identityCommitted, identityDiffers, initialized, logoutLatched, requestedSubject]);

  useEffect(() => {
    purgeLegacyCaches();
    purgeLegacyAppDataTenantCaches();
    sweepExpiredCache('localStorage');
    sweepExpiredCache('sessionStorage');
  }, []);

  useEffect(() => {
    if (!initialized || identityDiffers || logoutLatched) return;

    if (authenticatedClient) {
      // One-shot adoption of the pre-session-module unscoped last-visited hint
      // under the committed subject's key; idempotent once the legacy keys are
      // gone. Lives here rather than in the getter so reads stay pure.
      migrateLegacyLastVisitedOrganization();
      startProactiveTokenRefresh(authenticatedClient);
      void getOrganizationList().catch(() => {
        // The guards and selectors own visible error presentation.
      });
    } else {
      stopProactiveTokenRefresh();
    }
  }, [authenticatedClient, identityDiffers, initialized, logoutLatched]);

  useEffect(
    () => () => {
      stopProactiveTokenRefresh();
    },
    []
  );

  if (logoutLatched) {
    return (
      <FullPageError
        // A succeeding sign-out is a status, not a failure; the Retry
        // affordance stays for the stalled-redirect case.
        severity='info'
        message={t('Signing out… If this page does not redirect, use Retry to reload it.')}
        retrying={false}
        onRetry={() => window.location.reload()}
      />
    );
  }

  if (identityBoundaryPending) return null;

  const sessionKey = `session-boundary:${getSessionBoundaryGeneration()}`;
  return React.createElement(React.Fragment, { key: sessionKey }, children);
};
